import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Search,
  Filter,
  MoreVertical,
  Check,
  X
} from 'lucide-react';
import { projectAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ManagerProjectManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const { data: projects, isLoading } = useQuery(
    ['manager-projects', debouncedSearchTerm],
    () => projectAPI.getManagerProjects({ search: debouncedSearchTerm }),
    {
      select: (response) => response.data || [],
    }
  );

  // Fetch detailed project information when a project is selected
  const { data: detailedProject, isLoading: loadingDetails } = useQuery(
    ['manager-project-details', selectedProject?.id],
    () => projectAPI.getManagerProjectEmployees(selectedProject.id),
    {
      enabled: !!selectedProject?.id,
      select: (response) => {
        console.log('Manager project employees response:', response);
        // The manager endpoint returns the data directly, not wrapped in a data property
        return response.data || [];
      },
    }
  );

  const removeAssignmentMutation = useMutation(
    ({ projectId, employeeId }) => projectAPI.removeManagerEmployeeFromProject(projectId, employeeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('manager-projects');
        queryClient.invalidateQueries(['manager-project-details', selectedProject?.id]);
        toast.success('Employee removed from project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove employee');
      },
    }
  );

  const handleRemoveEmployee = (projectId, employeeId) => {
    if (window.confirm('Are you sure you want to remove this employee from the project?')) {
      removeAssignmentMutation.mutate({ projectId, employeeId });
    }
  };

  const openManageModal = (project) => {
    setSelectedProject(project);
    setShowManageModal(true);
  };

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Projects</h1>
          <p className="text-gray-600 mt-1">View your assigned projects and team members</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects?.map((project) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.name}</h3>
                {project.description && (
                  <p className="text-gray-600 text-sm line-clamp-2">{project.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{project.assignedEmployeesCount || 0} employees</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {project.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Manager Information */}
            {project.manager && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {project.manager.firstName?.charAt(0)}{project.manager.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Manager: {project.manager.firstName} {project.manager.lastName}
                    </p>
                    <p className="text-xs text-blue-600">{project.manager.email}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openManageModal(project)}
                className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center space-x-1"
              >
                <Users className="w-4 h-4" />
                <span>View Employees</span>
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Manage Employees Modal */}
      {showManageModal && selectedProject && (
        <ManageEmployeesModal
          project={selectedProject}
          projectDetails={detailedProject}
          currentUser={user}
          onClose={() => {
            setShowManageModal(false);
            setSelectedProject(null);
          }}
          onRemoveEmployee={handleRemoveEmployee}
          isLoading={removeAssignmentMutation.isLoading}
        />
      )}
    </div>
  );
};

// Manage Employees Modal Component
const ManageEmployeesModal = ({ project, projectDetails, currentUser, onClose, onRemoveEmployee, isLoading }) => {
  if (!projectDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4"
        >
          <div className="flex items-center justify-center h-32">
            <div className="spinner"></div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Helper function to check if current user can remove this employee
  const canRemoveEmployee = (employee) => {
    if (!currentUser || !employee.assignedByManagerName) {
      console.log('Debug - Cannot remove:', { 
        hasCurrentUser: !!currentUser, 
        assignedByManagerName: employee.assignedByManagerName 
      });
      return false;
    }
    const currentUserFullName = `${currentUser.firstName} ${currentUser.lastName}`;
    const canRemove = employee.assignedByManagerName === currentUserFullName;
    console.log('Debug - Remove check:', { 
      currentUserFullName, 
      assignedByManagerName: employee.assignedByManagerName,
      canRemove,
      employeeName: `${employee.firstName} ${employee.lastName}`
    });
    return canRemove;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Manage Employees - {project.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 text-sm">
            {project.description || 'No description provided'}
          </p>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Assigned Employees ({projectDetails?.length || 0})
          </h3>
          
          {projectDetails && projectDetails.length > 0 ? (
            <div className="space-y-2">
              {projectDetails.map((employee) => {
                const canRemove = canRemoveEmployee(employee);
                return (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-700 font-medium text-sm">
                          {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{employee.email}</p>
                        {employee.assignedByManagerName && (
                          <div className="mt-1 flex items-center space-x-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                              canRemove 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              👤 Assigned by: {employee.assignedByManagerName}
                              {canRemove && ' (You)'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {canRemove ? (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onRemoveEmployee(project.id, employee.id)}
                        disabled={isLoading}
                        className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center space-x-1"
                      >
                        <X className="w-4 h-4" />
                        <span>Remove</span>
                      </motion.button>
                    ) : (
                      <div className="px-3 py-1 text-sm text-gray-400 bg-gray-100 rounded-lg flex items-center space-x-1 cursor-not-allowed">
                        <X className="w-4 h-4" />
                        <span>Can't Remove</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No employees assigned to this project</p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ManagerProjectManagement;